import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import TechnicianCard from './components/TechnicianCard';
import ServiceCard from './components/ServiceCard';
import CalendarView from './components/CalendarView';
import PerformanceMetrics from './components/PerformanceMetrics';
import FilterPanel from './components/FilterPanel';
import CreateOrderModal from './components/CreateOrderModal';
import TechnicianTimeOffModal from './components/TechnicianTimeOffModal'; // Nouvel import
import Button from '../../components/ui/Button';
import { trackingService } from '../../services/trackingService';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const DispatchControlCenter = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('calendar');
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false); // Nouvel état
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Real-time state from Supabase
  const [technicians, setTechnicians] = useState([]);
  const [services, setServices] = useState([]);
  const [timeOffEvents, setTimeOffEvents] = useState([]); // Nouvel état pour les absences
  const [currentUser, setCurrentUser] = useState(null); // État pour l'utilisateur actuel
  const [filters, setFilters] = useState({
    urgency: 'all',
    status: 'all',
    technician: 'all',
    serviceType: 'all',
    area: '',
    search: ''
  });

  const mockMetrics = {
    etaAccuracy: { value: 92, trend: 3 },
    appointmentAdherence: { value: 96, trend: 1 },
    noShowRate: { value: 4, trend: -1 },
    avgInterventionTime: { value: 87, trend: -5 }
  };

  const serviceTypeOptions = [
    { value: 'leak', label: 'Fuite d\'eau' },
    { value: 'installation', label: 'Installation' },
    { value: 'unclog', label: 'Débouchage' },
    { value: 'repair', label: 'Réparation' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'heating', label: 'Chauffage' },
    { value: 'emergency', label: 'Urgence' }
  ];

  // Charger les absences depuis Supabase
  const loadTimeOffEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('technician_timeoff')
        .select('*')
       

      if (error) throw error;
      setTimeOffEvents(data || []);
    } catch (err) {
      console.error('Error loading time off events:', err);
    }
  };

  // Load initial data for selected date
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Charger les techniciens depuis user_profiles
        const techData = await trackingService.getAllTechnicians();
        
        // Charger les appointments pour la date sélectionnée
        const serviceData = await trackingService.getAppointmentsByDate(selectedDate);
        
        // Charger les absences
        await loadTimeOffEvents();

        // Transformer les données techniciens avec comptage et statut
        const techsWithStats = await Promise.all(
          techData.map(async (tech) => {
            const todayCount = await trackingService.getTechnicianAppointmentCount(
              tech.id,
              selectedDate
            );
            const status = await trackingService.getTechnicianStatus(tech.id);

            // Vérifier si le technicien a une absence aujourd'hui
            const isOnTimeOff = timeOffEvents.some(event => 
              event.technician_id === tech.id &&
              new Date(event.start_date) <= new Date(selectedDate) &&
              new Date(event.end_date || event.start_date) >= new Date(selectedDate)
            );

            return {
              id: tech.id,
              name: tech.name,
              avatar: tech.avatarUrl,
              avatarAlt: `Professional headshot of ${tech.name}`,
              specialty: tech.specialty,
              status: isOnTimeOff ? 'timeoff' : status,
              currentLocation: 'Unknown',
              todayServices: todayCount,
              maxServices: tech.maxDailyServices,
              rating: tech.rating,
              reviewCount: tech.reviewCount,
              email: tech.email,
              phone: tech.phone
            };
          })
        );

        setTechnicians(techsWithStats);
        setServices(serviceData);

      } catch (err) {
        setError(err?.message || 'Failed to load dispatch data');
        console.error('Error loading dispatch data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [selectedDate]);

  // Subscribe to real-time updates
  useEffect(() => {
    // Subscribe aux changements d'appointments
    const unsubscribeAppointments = trackingService.subscribeToAppointmentChanges(
      async (update) => {
        try {
          // Recharger les appointments pour la date sélectionnée
          const serviceData = await trackingService.getAppointmentsByDate(selectedDate);
          setServices(serviceData);

          // Mettre à jour le comptage du technicien si nécessaire
          if (update.technician) {
            const todayCount = await trackingService.getTechnicianAppointmentCount(
              update.technician.id,
              selectedDate
            );
            const status = await trackingService.getTechnicianStatus(update.technician.id);

            setTechnicians((prev) =>
              prev.map((tech) =>
                tech.id === update.technician.id
                  ? {
                      ...tech,
                      todayServices: todayCount,
                      status: status
                    }
                  : tech
              )
            );
          }
        } catch (err) {
          console.error('Error handling appointment update:', err);
        }
      }
    );

    // Subscribe aux changements de techniciens (user_profiles)
    const unsubscribeTechs = trackingService.subscribeToAllTechnicianLocations(
      (locationUpdate) => {
        setTechnicians((prev) =>
          prev.map((tech) =>
            tech.id === locationUpdate.technicianId
              ? {
                  ...tech,
                  status: locationUpdate.status,
                  name: locationUpdate.name || tech.name,
                  email: locationUpdate.email || tech.email,
                  phone: locationUpdate.phone || tech.phone
                }
              : tech
          )
        );
      }
    );

    // Subscribe aux changements d'absences
    const timeOffSubscription = supabase
      .channel('technician_timeoff_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'technician_timeoff' },
        () => {
          loadTimeOffEvents();
        }
      )
      .subscribe();

    return () => {
      if (unsubscribeAppointments) unsubscribeAppointments();
      if (unsubscribeTechs) unsubscribeTechs();
      timeOffSubscription.unsubscribe();
    };
  }, [selectedDate]);

  // Nouvelle fonction pour gérer la soumission d'une absence
  const handleTimeOffSubmit = async (timeOffData) => {
    try {
      // Sauvegarder dans Supabase
      const { data, error } = await supabase
        .from('technician_timeoff')
        .insert([{
          technician_id: timeOffData.technicianId,
          absence_type: timeOffData.absenceType,
          start_date: timeOffData.startDate,
          end_date: timeOffData.endDate || null,
          start_time: timeOffData.isAllDay ? null : timeOffData.startTime,
          end_time: timeOffData.isAllDay ? null : timeOffData.endTime,
          is_all_day: timeOffData.isAllDay,
          is_recurring: timeOffData.isRecurring,
          recurring_type: timeOffData.recurringType,
          reason: timeOffData.reason || null,
          color: 'error',
          status: 'timeoff'
        }])
        .select();

      if (error) throw error;

      // Recharger les absences
      await loadTimeOffEvents();
      
      // Mettre à jour le statut des techniciens concernés
      setTechnicians(prev => 
        prev.map(tech => 
          tech.id === timeOffData.technicianId
            ? { ...tech, status: 'timeoff' }
            : tech
        )
      );

      setIsTimeOffModalOpen(false);
    } catch (err) {
      console.error('Error saving time off:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      urgency: 'all',
      status: 'all',
      technician: 'all',
      serviceType: 'all',
      area: '',
      search: ''
    });
  };

  const handleAssignService = async (technicianId) => {
    console.log('Assigning service to technician:', technicianId);
    // TODO: Implémenter l'assignation
  };

  const handleCallTechnician = (technicianId) => {
    const technician = technicians.find(t => t.id === technicianId);
    if (technician?.phone) {
      window.location.href = `tel:${technician.phone}`;
    }
  };

  const handleContactCustomer = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service?.customerPhone) {
      window.location.href = `tel:${service.customerPhone}`;
    }
  };

  const handleReassignService = async (serviceId) => {
    console.log('Reassigning service:', serviceId);
    // TODO: Implémenter la réassignation
  };

  const handleViewServiceDetails = (serviceId) => {
    console.log('Viewing service details:', serviceId);
    // TODO: Ouvrir modal avec détails
  };

  const handleAppointmentClick = (appointment) => {
    console.log('Appointment clicked:', appointment);
    handleViewServiceDetails(appointment.id);
  };

  const handleCreateOrder = async (orderData) => {
    console.log('Creating new order:', orderData);
    try {
      // Vérifier si le technicien est disponible
      if (orderData.assignedTechnician) {
        const technician = technicians.find(t => t.id === orderData.assignedTechnician);
        if (technician?.status === 'timeoff') {
          alert('Ce technicien est en congé. Veuillez en choisir un autre.');
          return;
        }
      }

      // Créer l'appointment dans Supabase
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .insert([{
          customer_name: orderData.customerName,
          customer_phone: orderData.customerPhone,
          customer_email: orderData.customerEmail || null,
          address: orderData.address,
          service_type: orderData.serviceType,
          urgency: orderData.urgency,
          scheduled_date: orderData.scheduledDate,
          scheduled_time: orderData.scheduledTime,
          technician_id: orderData.assignedTechnician || null,
          description: orderData.description || null,
          is_quote_based: orderData.isQuoteBased || false,
          estimated_price: orderData.estimatedPrice || null,
          status: 'scheduled'
        }])
        .select();

      if (appointmentError) throw appointmentError;

      setIsCreateOrderModalOpen(false);
      
      // Recharger les données
      const serviceData = await trackingService.getAppointmentsByDate(selectedDate);
      setServices(serviceData);
    } catch (err) {
      console.error('Error creating order:', err);
      alert('Erreur lors de la création de la commande');
    }
  };

  const handleDateChange = async (newDate) => {
    setSelectedDate(newDate);
  };

  const handleLogout = () => {
    navigate('/');
  };

  const filteredServices = services?.filter((service) => {
    if (filters?.urgency !== 'all' && service?.urgency !== filters?.urgency) return false;
    if (filters?.status !== 'all' && service?.status !== filters?.status) return false;
    if (filters?.technician !== 'all' && service?.technicianId !== filters?.technician) return false;
    if (filters?.serviceType !== 'all' && service?.serviceType !== filters?.serviceType) return false;
    if (filters?.search && !service?.customerName?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    return true;
  });

  const filteredTechnicians = technicians?.filter((tech) => {
    if (filters?.technician !== 'all' && tech?.id !== filters?.technician) return false;
    return true;
  });

  // Statistiques des absences
  const timeOffStats = {
    today: timeOffEvents.filter(event => 
      new Date(event.start_date) <= new Date(selectedDate) &&
      new Date(event.end_date || event.start_date) >= new Date(selectedDate)
    ).length,
    upcoming: timeOffEvents.filter(event => 
      new Date(event.start_date) > new Date(selectedDate)
    ).length
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
            <h2 className="text-xl font-semibold text-foreground">Erreur de chargement</h2>
          </div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location?.reload()} variant="outline">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
          
      />
{/* <RoleBasedHeader
        userRole={currentUser?.role}
        userName={`${currentUser?.name} - ${APP_NAME}`}
                onLogout={handleLogout}

      /> */}
      <main className="main-content">
        <div className="container mx-auto px-4 py-6 max-w-[1600px]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Centre de Dispatch</h1>
              <p className="text-muted-foreground">
                Gérez les interventions et coordonnez les techniciens en temps réel
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Nouveau bouton pour les absences */}
              <Button
                variant="outline"
                size="lg"
                iconName="CalendarX"
                onClick={() => setIsTimeOffModalOpen(true)}
                className="border-error/20 text-error hover:bg-error/10 relative"
              >
                Indisponibilité
                {/* {timeOffStats.today > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs rounded-full flex items-center justify-center">
                    {timeOffStats.today}
                  </span>
                )} */}
              </Button>
              <Button
                variant="default"
                size="lg"
                iconName="Plus"
                onClick={() => setIsCreateOrderModalOpen(true)}
              >
                Nouvelle commande
              </Button>
            </div>
          </div>

          {/* Badges d'information */}
          <div className="flex gap-4 mb-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-error/10 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-error" />
              <span className="text-sm text-foreground">Absents aujourd'hui: {timeOffStats.today}</span>
            </div>
            {/* <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-warning" />
              <span className="text-sm text-foreground">Absences à venir: {timeOffStats.upcoming}</span>
            </div> */}
          </div>

          <PerformanceMetrics metrics={mockMetrics} />

          <div className="mt-6">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              technicians={technicians}
              serviceTypes={serviceTypeOptions}
            />
          </div>

          <div className="mt-6 flex gap-2 mb-4">
            <Button
              variant={activeView === 'calendar' ? 'default' : 'outline'}
              iconName="Calendar"
              onClick={() => setActiveView('calendar')}
            >
              Vue calendrier
            </Button>
            <Button
              variant={activeView === 'list' ? 'default' : 'outline'}
              iconName="List"
              onClick={() => setActiveView('list')}
            >
              Vue liste
            </Button>
            <Button
              variant={activeView === 'map' ? 'default' : 'outline'}
              iconName="Map"
              onClick={() => setActiveView('map')}
            >
              Vue carte
            </Button>
          </div>

          {activeView === 'calendar' && (
            <CalendarView
              appointments={filteredServices}
              technicians={filteredTechnicians}
              timeOffEvents={timeOffEvents} // Passer les absences au calendrier
              onAppointmentClick={handleAppointmentClick}
              onDateChange={handleDateChange}
              selectedDate={selectedDate}
            />
          )}

          {activeView === 'list' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-card rounded-lg border border-border p-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Interventions actives ({filteredServices?.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredServices?.map((service) => (
                      <ServiceCard
                        key={service?.id}
                        service={service}
                        onViewDetails={handleViewServiceDetails}
                        onReassign={handleReassignService}
                        onContactCustomer={handleContactCustomer}
                        onUpdateStatus={(serviceId, status) => console.log('Update status:', serviceId, status)}
                      />
                    ))}
                  </div>
                  {filteredServices?.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucune intervention pour cette date
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-card rounded-lg border border-border p-4">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Techniciens disponibles ({filteredTechnicians?.filter((t) => t?.status === 'available')?.length})
                  </h2>
                  <div className="space-y-3">
                    {filteredTechnicians?.map((technician) => (
                      <TechnicianCard
                        key={technician?.id}
                        technician={technician}
                        onAssignService={handleAssignService}
                        onCallTechnician={handleCallTechnician}
                        onViewDetails={(technicianId) => console.log('View technician details:', technicianId)}
                      />
                    ))}
                  </div>
                  {filteredTechnicians?.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucun technicien disponible
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeView === 'map' && (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Carte des interventions en temps réel"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=48.8566,2.3522&z=12&output=embed"
                  className="w-full h-full"
                />
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex items-center gap-2 p-3 bg-error/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <span className="text-sm text-foreground">Urgent</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-warning/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <span className="text-sm text-foreground">Aujourd'hui</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-sm text-foreground">Planifié</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-success" />
                  <span className="text-sm text-foreground">Terminé</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <QuickActionToolbar
        userRole="dispatcher"
        onEmergencyCall={() => console.log('Emergency call')}
        onQuickBooking={() => setIsCreateOrderModalOpen(true)}
        onSupportChat={() => console.log('Support chat')}
      />

      <CreateOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onSubmit={handleCreateOrder}
        technicians={technicians}
        serviceTypes={serviceTypeOptions}
      />

      {/* Nouveau modal d'indisponibilité */}
      <TechnicianTimeOffModal
        isOpen={isTimeOffModalOpen}
        onClose={() => setIsTimeOffModalOpen(false)}
        technicians={technicians}
        onSubmit={handleTimeOffSubmit}
      />
    </div>
  );
};

export default DispatchControlCenter;